import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { ArrowRight, Check, Copy, Download, Link2Off, RefreshCw, Share2 } from 'lucide-react'
import { api } from '@/util/api'
import { Badge, Button, ButtonLink, Collapsible, CopyField, EmptyState, IconButton, Notice, copyText, useToast } from '@/components/play'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { SiteFooter } from '@/components/SiteFooter'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { streamAppUrl } from '@/lib/appLink'
import { androidInstallUrl, appStores, invitePlatform } from '@/lib/androidApp'
import '@/components/onboarding/onboarding.css'

const APP_STORE_URL = 'https://apps.apple.com/us/app/streambridge/id6759164973'
const APP_STORE_BADGE_URL =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83'
const GOOGLE_PLAY_BADGE_URL = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
/** The web can't see whether the app is installed: still here after this long, it didn't take over. */
const APP_OPEN_WAIT_MS = 2500

/**
 * A phone with the app opens the invite in it, so a phone on this page probably has no
 * app. Getting it leads: the store is safe either way, where opening a missing app fails.
 * After the store, opening leads, and a failed open leads back.
 */
type AppStep = 'get' | 'open' | 'opening' | 'not-opened'

interface StreamData {
  name: string
  status: string
  event_id: string
  event_name: string | null
  stream_key: string
  srt_ingress_url?: string | null
}

export function StreamInvitePage() {
  const { streamKey } = useParams<{ streamKey: string }>()
  const [stream, setStream] = useState<StreamData | null>(null)
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)
  const [copied, setCopied] = useState(false)
  const [appStep, setAppStep] = useState<AppStep>('get')
  const { toast } = useToast()
  const platform = invitePlatform(navigator.userAgent, navigator.maxTouchPoints)
  const runsApp = platform !== 'unknown'
  const stores = appStores(platform)
  const inviteUrl = stream
    ? `${window.location.origin}/stream/${encodeURIComponent(streamKey ?? '')}?event=${encodeURIComponent(stream.event_id)}`
    : ''
  const deepLinkUrl = stream ? streamAppUrl(streamKey || '', stream.event_id, window.location.hostname) : ''
  const openLeads = appStep === 'open' || appStep === 'opening'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setStream(null)
    setError(null)
    setUnavailable(false)
    if (!streamKey) {
      setUnavailable(true)
      setLoading(false)
      return
    }
    api
      .get<{ data: { attributes: StreamData } }>(
        `/public/streams/by-key/${encodeURIComponent(streamKey)}`,
      )
      .then((response) => {
        if (!cancelled) setStream(response.data.attributes)
      })
      .catch((err: any) => {
        if (cancelled) return
        if (err?.response?.status === 404) setUnavailable(true)
        else setError('Check your connection and try again.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [streamKey, retry])

  useEffect(() => {
    if (!copied) return
    const timeout = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timeout)
  }, [copied])

  useEffect(() => {
    if (appStep !== 'opening') return
    const timeout = window.setTimeout(() => setAppStep('not-opened'), APP_OPEN_WAIT_MS)
    return () => window.clearTimeout(timeout)
  }, [appStep])

  useEffect(() => {
    // iOS suspends the page unannounced and reports the switch on return, when the page is
    // already visible, so the event itself is the signal. Blur isn't: Safari's alert for a
    // missing app blurs the page too.
    function backFromApp() {
      setAppStep((step) => (step === 'get' ? step : 'open'))
    }
    document.addEventListener('visibilitychange', backFromApp)
    return () => document.removeEventListener('visibilitychange', backFromApp)
  }, [])

  async function copyInvite() {
    if (await copyText(inviteUrl)) {
      setCopied(true)
    } else {
      toast({
        title: 'Couldn’t copy the link',
        description: 'Select the link in connection details and copy it.',
        tone: 'error',
      })
    }
  }

  async function share() {
    if (!navigator.share) {
      await copyInvite()
      return
    }
    try {
      await navigator.share({
        title: `${stream?.name} · ${stream?.event_name}`,
        text: 'Open your StreamBridge camera invite.',
        url: inviteUrl,
      })
    } catch (err: unknown) {
      if (!(err instanceof Error && err.name === 'AbortError'))
        toast({
          title: 'Couldn’t share the link',
          description: 'Copy the invite link instead.',
          tone: 'error',
        })
    }
  }

  return (
    <div className="play-theme play-entry">
      <header className="play-entry-header">
        <Link to="/" aria-label="StreamBridge home">
          <PlayLogo className="w-40" />
        </Link>
      </header>
      <main className="play-invite-page">
        {loading && (
          <ContentSkeleton label="Finding your camera" variant="qr" className="py-12" />
        )}
        {/* With nothing to show, the page's heading is for screen readers; the state's own title follows it. */}
        {(unavailable || error) && <h1 className="sr-only">Camera invite</h1>}
        {unavailable && (
          <EmptyState
            icon={<Link2Off />}
            title="This camera invite is no longer available"
            description="Ask the organizer for a new link."
          />
        )}
        {error && (
          <Notice
            tone="error"
            title="We couldn’t load this camera"
            action={
              <Button variant="secondary" onClick={() => setRetry((value) => value + 1)}>
                <RefreshCw aria-hidden="true" />
                Try again
              </Button>
            }
          >
            {error}
          </Notice>
        )}
        {!loading && !error && stream && (
          <section className="play-invite-pass" aria-labelledby="invite-event">
            <Badge>You’re invited</Badge>
            <h1 id="invite-event" className="play-invite-pass__event">
              {stream.event_name || 'StreamBridge event'}
            </h1>
            <h2 className="play-invite-pass__camera">{stream.name}</h2>

            {runsApp && (
              <div className="play-invite-pass__option">
                <ol className="play-invite-pass__steps">
                  <li className="play-invite-pass__step">
                    <ButtonLink
                      href={platform === 'ios' ? APP_STORE_URL : androidInstallUrl(inviteUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant={openLeads ? 'secondary' : 'primary'}
                      className="w-full justify-between"
                      onClick={() => {
                        if (platform === 'ios') void copyText(inviteUrl)
                        setAppStep('open')
                      }}
                    >
                      Get Stream app
                      <Download aria-hidden="true" />
                    </ButtonLink>
                  </li>
                  <li className="play-invite-pass__step">
                    <ButtonLink
                      href={deepLinkUrl}
                      variant={openLeads ? 'primary' : 'secondary'}
                      className="w-full justify-between"
                      onClick={() => setAppStep('opening')}
                    >
                      Open Stream app
                      <ArrowRight aria-hidden="true" />
                    </ButtonLink>
                  </li>
                </ol>
                <p className="play-invite-pass__label" role="status">
                  {appStep === 'not-opened' ? 'Didn’t open? Get Stream app first.' : ''}
                </p>
              </div>
            )}

            <div className="play-invite-pass__qr">
              <QRCodeSVG
                value={inviteUrl}
                size={216}
                marginSize={0}
                level="M"
                title={`${stream.name} camera invite`}
              />
            </div>

            <div className="play-invite-pass__actions">
              <IconButton
                variant="ghost"
                label={copied ? 'Invite copied' : 'Copy camera invite'}
                onClick={() => void copyInvite()}
              >
                {copied ? <Check /> : <Copy />}
              </IconButton>
              <IconButton variant="ghost" label="Share camera invite" onClick={share}>
                <Share2 />
              </IconButton>
            </div>

            {!runsApp && (
              <div className="play-invite-pass__install">
                {stores.appStore && (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Install Stream on the App Store"
                    className="play-app-store-badge"
                    // The invite rides the clipboard through the App Store
                    // install; the app offers Paste invite on first launch.
                    onClick={() => {
                      void copyText(inviteUrl)
                    }}
                  >
                    <img src={APP_STORE_BADGE_URL} alt="" width={120} height={40} />
                  </a>
                )}
                {/* Internal testing for now; then the listing, with the invite as install referrer for the first launch. */}
                {stores.googlePlay && (
                  <a
                    href={androidInstallUrl(inviteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Install Stream on Google Play"
                    className="play-app-store-badge play-app-store-badge--google-play"
                  >
                    <img src={GOOGLE_PLAY_BADGE_URL} alt="" width={155} height={60} />
                  </a>
                )}
              </div>
            )}

            <Collapsible className="play-invite-details" trigger="Connection details" keepMounted>
              {[
                { label: 'Invite link', value: inviteUrl },
                ...(stream.srt_ingress_url ? [{ label: 'SRT camera input', value: stream.srt_ingress_url }] : []),
              ].map(({ label, value }) => <CopyField key={label} label={label} value={value} />)}
            </Collapsible>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
